import { RichTextService } from 'src/app/shared/services/rich-text/rich-text.service';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { UrlEnum } from '../../enum/url.enum';
import { HttpRequestService } from './../http-request/http-request.service';
import { ArticleNewsModel, CommentModel } from '../../model/article.model';
import { UtilitiesService } from '../utilities/utilities.service';

@Injectable({
  providedIn: 'root',
})
export class ShohayBlogService {
  private blogUrl = `${UrlEnum.SHOHAY_BLOG}/blog`;
  private commentUrl = `${UrlEnum.SHOHAY_BLOG}/comment`;
  private tagUrl = `${UrlEnum.SHOHAY_BLOG}/tag`;

  constructor(
    private readonly richTextService: RichTextService,
    private readonly utilitiesService: UtilitiesService,
    private readonly httpRequestService: HttpRequestService
  ) {}

  public getAllArticleNews(isPublished: boolean = false): Observable<any> {
    return this.httpRequestService
      .get(`${this.blogUrl}/get-all${isPublished ? '-published' : ''}`, {
        skipAuth: true,
      })
      .pipe(
        map((x: any) => this.utilitiesService.responseHandler(x)),
        map((response: any[]) => this.decodeAllResponses(response))
      );
  }

  private decodeAllResponses(response: any[]): any[] {
    response?.forEach((item: any) => {
      item.cover_photo = this.utilitiesService.jsonParse(item.cover_photo);
      item.tags = this.utilitiesService
        .jsonParse(item.tags)
        ?.map((tag) => tag.name)
        .join(', ');
    });
    return response;
  }

  public getArticleById(id: string): Observable<any> {
    return this.httpRequestService
      .get(`${this.blogUrl}/get-by-id/${id}`, { skipAuth: true })
      .pipe(
        map((x: any) => this.utilitiesService.responseHandler(x)),
        map((response: any) => this.decodeResponse(response))
      );
  }

  private decodeResponse(response: any): any {
    response.cover_photo = this.utilitiesService.jsonParse(
      response.cover_photo
    );
    response.table_of_contents = this.utilitiesService.jsonParse(
      response.table_of_contents
    );
    response.tags = this.utilitiesService.jsonParse(response.tags);

    response.table_of_contents.forEach((item: any) => {
      item.description = this.richTextService.decodeHtml(item.description);
    });
    return response;
  }

  public createArticleNewsBlog(body: ArticleNewsModel): Observable<any> {
    return this.httpRequestService
      .post(`${this.blogUrl}/create`, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public updateArticleNewsBlog(
    articleId: string,
    body: ArticleNewsModel
  ): Observable<any> {
    return this.httpRequestService
      .patch(`${this.blogUrl}/update/${articleId}`, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public publishArticleNews(articleId: string, publish): Observable<any> {
    return this.httpRequestService
      .patch(`${this.blogUrl}/update-publish-blog/${articleId}`, {
        is_published: publish,
      })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public deleteArticleNews(articleId: string): Observable<any> {
    return this.httpRequestService
      .delete(`${this.blogUrl}/delete/${articleId}`)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getAllTags(): Observable<any> {
    return this.httpRequestService.get(`${this.tagUrl}/get-all`).pipe(
      map((x: any) => this.utilitiesService.responseHandler(x)),
      map((tags: any[]) => tags.map((tag) => ({ id: tag.id, name: tag.name })))
    );
  }

  public createTag(tag: string): Observable<any> {
    return this.httpRequestService
      .post(`${this.tagUrl}/create/`, { name: tag })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getComments(articleNewsId: string): Observable<any> {
    return this.httpRequestService
      .get(`${this.commentUrl}/get-by-blog/${articleNewsId}`, {
        skipAuth: true,
      })
      .pipe(
        map((x: any) => this.utilitiesService.responseHandler(x))
      );
  }

  public postComment(comment: CommentModel): Observable<any> {
    return this.httpRequestService
      .post(`${this.commentUrl}/create`, comment)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public replyComment(
    comment: CommentModel,
    parentCommentId: string
  ): Observable<any> {
    return this.httpRequestService
      .post(`${this.commentUrl}/create-reply/${parentCommentId}`, comment)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public deleteComment(commentId: string): Observable<any> {
    return this.httpRequestService
      .delete(`${this.commentUrl}/delete/${commentId}`)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }
}
